import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * <SEO />
 * Dynamically updates <title>, meta description, canonical, OG and Twitter tags
 * per route + language. Runs on the client (SPA); static crawlers still get
 * the base tags from /public/index.html.
 */

const SITE_URL = "https://sprintroll.app";
const OG_IMAGE_ES = `${SITE_URL}/og-image.jpg`;
const OG_IMAGE_EN = `${SITE_URL}/og-image-en.jpg`;

function upsertMeta({ name, property, content }) {
  if (content == null) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink({ rel, href, hreflang }) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({
  titleKey,
  descriptionKey,
  path = "/",
  image,
  noindex = false,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "es").split("-")[0];

  useEffect(() => {
    const title = titleKey ? t(titleKey) : t("seo.default.title");
    const description = descriptionKey ? t(descriptionKey) : t("seo.default.description");
    const url = `${SITE_URL}${path}`;
    const ogImage = image || (lang === "en" ? OG_IMAGE_EN : OG_IMAGE_ES);
    const locale = lang === "en" ? "en_US" : "es_ES";
    const altLocale = lang === "en" ? "es_ES" : "en_US";

    // <html lang>
    document.documentElement.setAttribute("lang", lang);

    // Title + primary meta
    document.title = title;
    upsertMeta({ name: "description", content: description });
    upsertMeta({
      name: "robots",
      content: noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large",
    });

    // Canonical + hreflang
    upsertLink({ rel: "canonical", href: url });
    upsertLink({ rel: "alternate", hreflang: "es", href: url });
    upsertLink({ rel: "alternate", hreflang: "en", href: url });
    upsertLink({ rel: "alternate", hreflang: "x-default", href: url });

    // Open Graph
    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:url", content: url });
    upsertMeta({ property: "og:image", content: ogImage });
    upsertMeta({ property: "og:image:secure_url", content: ogImage });
    upsertMeta({ property: "og:type", content: "website" });
    upsertMeta({ property: "og:site_name", content: "Sprintroll" });
    upsertMeta({ property: "og:locale", content: locale });
    upsertMeta({ property: "og:locale:alternate", content: altLocale });

    // Twitter
    upsertMeta({ name: "twitter:card", content: "summary_large_image" });
    upsertMeta({ name: "twitter:title", content: title });
    upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: ogImage });
    upsertMeta({ name: "twitter:url", content: url });
  }, [t, lang, titleKey, descriptionKey, path, image, noindex]);

  return null;
}
